import { stringify } from "querystring";

import { useCallback, useContext } from "react";

import { FrontendRoutes, RouteBuilders, CampaignAction, FormType } from "@simplyk/common";
import { useSearchParams } from "next/navigation";

import { unknownPaymentErrorCode } from "../components/PaymentElement/helper";
import { isTicketingData } from "../components/PaymentProcessor/helper";
import { FrontendFormContext } from "../contexts/FrontendFormContext";
import { FrontendTicketingContext } from "../contexts/FrontendTicketingContext";
import { useLocaleContext } from "../contexts/LocaleContext";
import { formatCreatedCampaigns } from "../helpers/navigateToPeerToPeerSuccess";
import { getFundraisingCreationInput } from "../helpers/payment";
import { trpc } from "../helpers/trpc";
import { FRONTEND_URL } from "../routes/routes";

import { useTranslate } from "./useTranslate";
import { useUrlBuilder } from "./useUrlBuilder";

import { setObjectInLocalStorage } from "@/helpers/localStorage";
import { DonationFormPaymentInput } from "@/types/donationForm";
import { TicketingPaymentInput } from "@/types/ticketing";
import { CreateRegistrationCampaignsOutput } from "@/types/trpc";

interface CreateCampaignsProps {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  paymentInput: TicketingPaymentInput;
  commandId?: string | null;
}

export const useCreateRegistrationCampaigns = () => {
  const { locale, isoLocale } = useLocaleContext();
  const { ticketing, isCampaignRegistration } = useContext(FrontendTicketingContext);
  const { commandId, organization, postTransactionUrl } = useContext(FrontendFormContext);

  const { t } = useTranslate();
  const buildUrl = useUrlBuilder();
  const searchParams = useSearchParams();
  const query = Object.fromEntries(searchParams.entries());
  const { isPeerToPeerV2 } = query;

  const { mutateAsync: createRegistrationCampaigns } = trpc.createRegistrationCampaigns.useMutation();

  // will return true if the user is paying a registration on a p2p on a v1 ticketing form
  const shouldCreateCampaigns = useCallback(
    (
      paymentInput: TicketingPaymentInput | DonationFormPaymentInput,
      formType: FormType
    ): paymentInput is TicketingPaymentInput =>
      isTicketingData(paymentInput, formType) &&
      isCampaignRegistration &&
      // Use V1 behavior (create campaigns) for forms created before 2025-09-21
      Boolean(ticketing.createdAtUtc && new Date(ticketing.createdAtUtc) < new Date("2025-09-21")),
    [isCampaignRegistration, ticketing.createdAtUtc]
  );

  // will return true if the user is paying a registration on a p2p on a v2 ticketing form
  const shouldNotCreateCampaignButRedirectToPeerToPeer = useCallback(
    (
      paymentInput: TicketingPaymentInput | DonationFormPaymentInput,
      formType: FormType
    ): paymentInput is TicketingPaymentInput =>
      isTicketingData(paymentInput, formType) &&
      isCampaignRegistration &&
      // Use V2 behavior (redirect) for forms created after 2025-09-21
      Boolean(ticketing.createdAtUtc && new Date(ticketing.createdAtUtc) >= new Date("2025-09-21")),
    [isCampaignRegistration, ticketing.createdAtUtc]
  );

  // will return the url to which the user that has paid a registration on a p2p on a v2 ticketing form will be redirected to
  const createPeerToPeerV2RedirectUrl = useCallback(
    async ({
      firstName,
      lastName,
      email,
      paymentInput: _paymentInput,
      commandId,
    }: CreateCampaignsProps): Promise<{ returnUrl?: string | null; error?: string | null }> => {
      if (!ticketing.donationForm?.campaignId) {
        return { error: "No associated peer-to-peer campaign found" };
      }

      // For V2 flow, redirect to the associated peer-to-peer campaign with openFundraiser=true
      const params = {
        openFundraiser: "true",
        firstName: firstName || "",
        lastName: lastName || "",
        email: email || "",
        commandId: commandId || "",
        paid: "true",
      };

      const fullReturnUrl = buildUrl({
        route: RouteBuilders.buildPeerToPeerV2CampaignLink({
          type: "general",
          isoLocale,
          path: ticketing.donationForm.campaignId,
        }),
        searchParams: params,
        includeRouterSearchParams: false,
        asExternalLink: true,
      });
      return { returnUrl: fullReturnUrl };
    },
    [ticketing, isoLocale, buildUrl]
  );

  const createCampaigns = useCallback(
    async ({
      firstName,
      lastName,
      email,
      paymentInput,
    }: CreateCampaignsProps): Promise<{ returnUrl?: string | null; error?: string | null }> => {
      const fundraisingInput = getFundraisingCreationInput(
        {
          firstName,
          lastName,
          email,
        },
        paymentInput,
        locale,
        organization.country
      );
      if (!fundraisingInput) {
        return { error: t("common", "unknownError", { code: unknownPaymentErrorCode.other5.code }) };
      }
      const { data } = await createRegistrationCampaigns({
        ticketingId: ticketing.id,
        commandId,
        fundraising: fundraisingInput,
      });
      if (!data) {
        return { error: t("common", "unknownError", { code: unknownPaymentErrorCode.other4.code }) };
      }
      const paymentSucceededData = formatCreatedCampaigns(data as CreateRegistrationCampaignsOutput);

      const params = {
        formId: paymentSucceededData?.id,
        teamId: paymentSucceededData?.teamId,
        campaignAction: paymentSucceededData?.campaignAction,
        isFundraiserFormEditDisabled: paymentSucceededData?.isFundraiserFormEditDisabled,
        color: paymentSucceededData?.color,
        organizationName: organization.name,
        logoUrl: paymentSucceededData?.logoUrl || undefined,
        multipleCampaigns: paymentSucceededData?.multipleCampaigns,
        token: paymentSucceededData?.token,
      };

      // Save token and formId in local storage
      setObjectInLocalStorage?.("peer-to-peer-form-data", {
        token: paymentSucceededData?.token,
        formId: paymentSucceededData?.id,
      });

      // For now, users that create campaign through registration are using the old flow, but we need to redirect them to the new form once payment is submitted
      const returnUrlV1 =
        postTransactionUrl ||
        `${process.env.NEXT_PUBLIC_FRONTEND_URL}/${isoLocale}${FrontendRoutes.PeerToPeerSuccess}?${stringify(params)}`;

      const returnUrlV2 =
        paymentSucceededData?.campaignAction === CampaignAction.IndividualFundraising
          ? `${FRONTEND_URL}${
              RouteBuilders.buildPeerToPeerV2IndividualCampaignLink({
                isoLocale,
                path: paymentSucceededData?.id || "",
              }).path
            }?new=true`
          : `${FRONTEND_URL}${
              RouteBuilders.buildPeerToPeerV2TeamCampaignLink({
                isoLocale,
                path: paymentSucceededData?.id || "",
              }).path
            }?new=true`;

      const returnUrl = isPeerToPeerV2 ? returnUrlV2 : returnUrlV1;

      return { returnUrl };
    },
    [
      commandId,
      createRegistrationCampaigns,
      isPeerToPeerV2,
      isoLocale,
      locale,
      organization.country,
      organization.name,
      postTransactionUrl,
      t,
      ticketing.id,
    ]
  );

  return {
    createCampaigns,
    shouldCreateCampaigns,
    shouldNotCreateCampaignButRedirectToPeerToPeer,
    createPeerToPeerV2RedirectUrl,
  };
};
